/**
 * Localizador de tiendas (sections/store-locator.liquid).
 *
 * - La lista ya viene del servidor; aquí solo se filtra (buscador + departamento) y se ordena
 *   por cercanía con "Cerca de mí" (geolocalización del navegador, sin mapa de por medio).
 * - Mientras tanto se ve la vista previa estática que arma la sección (imagen + pines). Los pines
 *   siguen a los filtros y un clic en uno abre esa tienda en el mapa interactivo.
 * - El mapa interactivo usa Leaflet (assets/leaflet.js/.css, ~45 KB gzip):
 *   `data-map-load="auto"`: se monta apenas el mapa está a menos de 300px de la vista. No espera
 *   al evento `load` de la página, que en The Box llega tarde por las apps (7 s en computadora,
 *   25 s en celular lento); la sección ya dejó Leaflet precargándose con prioridad baja.
 *   `data-map-load="click"`: solo con "Ver mapa". En ambos modos "Ver en mapa" lo carga al toque.
 * - La vista previa se desvanece recién cuando el mapa tiene sus mosaicos, para no mostrar un
 *   mapa gris a medio cargar.
 * - Gestos cooperativos (como Google Maps), para que el mapa no le robe el scroll a la página:
 *   computadora: la rueda sola desplaza la página; el pellizco del trackpad (llega como
 *   Ctrl + rueda) y Ctrl/⌘ + rueda hacen zoom en el mapa, nunca en la página (Safari manda el
 *   pellizco como `gesture*`, también se atiende). Celular: un dedo desplaza la página y dos
 *   dedos mueven y hacen zoom en el mapa. Si el gesto no es para el mapa, se muestra un aviso.
 * - En el editor de temas, seleccionar un bloque "Tienda" la centra en el mapa.
 */

// Proveedores de mosaicos que no piden API key. Carto dejó de servir sin key (sale una marca de
// agua "API KEY REQUIRED"), por eso ya no está. `labels` es una capa de nombres encima del fondo.
const OSM = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const ESRI = 'Tiles &copy; <a href="https://www.esri.com">Esri</a>';
const TILES = {
  osm: {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: OSM,
    maxZoom: 19,
  },
  hot: {
    url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
    attribution: `${OSM}, estilo <a href="https://www.hotosm.org/">HOT</a>`,
    subdomains: 'abc',
    maxZoom: 19,
  },
  esri_street: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: `${ESRI} — Esri, HERE, Garmin, ${OSM}`,
    maxZoom: 19,
  },
  esri_gray: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    labels: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}',
    attribution: `${ESRI} — Esri, HERE, Garmin, ${OSM}`,
    maxZoom: 16,
  },
};

const PIN_SVG =
  '<svg viewBox="0 0 30 40" aria-hidden="true"><path d="M15 0C6.7 0 0 6.6 0 14.8 0 25.9 15 40 15 40s15-14.1 15-25.2C30 6.6 23.3 0 15 0z"/><circle cx="15" cy="14.5" r="5.5" fill="#fff"/></svg>';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Contenedor que desplaza la página. En Horizon es la ventana, pero Ritual (Coolbrands) desplaza
 * `div.page-wrapper`, así que se busca el primer ancestro con scroll propio.
 * @param {Element} element
 * @returns {Element | Window}
 */
function scrollParent(element) {
  for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
  }
  return window;
}

/** Pantalla solo táctil (celular/tablet): ahí el mapa se mueve con dos dedos. */
const touchOnly = () => window.matchMedia('(hover: none) and (pointer: coarse)').matches;

const isMac = /Mac|iPhone|iPad/.test(navigator.userAgentData?.platform ?? navigator.platform ?? '');
const WHEEL_HINT = isMac
  ? 'Pellizca o usa ⌘ + desplazamiento para acercar el mapa'
  : 'Pellizca o usa Ctrl + desplazamiento para acercar el mapa';
const TOUCH_HINT = 'Usa dos dedos para mover el mapa';

/** Minúsculas y sin tildes, para que "brena" encuentre "Breña". @param {string | undefined} text */
const normalize = (text) =>
  (text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

/** Distancia en km entre dos puntos (fórmula de haversine). */
function distanceKm(lat1, lng1, lat2, lng2) {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLng = (lng2 - lng1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(a));
}

/** @param {number} km */
const formatDistance = (km) =>
  km < 1
    ? `${Math.round(km * 1000)} m`
    : `${km.toLocaleString('es-PE', { maximumFractionDigits: km < 10 ? 1 : 0 })} km`;

/** @type {Promise<any> | undefined} */
let leafletPromise;

/** Carga Leaflet una sola vez por página (JS + CSS en paralelo). */
function loadLeaflet(jsUrl, cssUrl) {
  if (window.L) return Promise.resolve(window.L);

  leafletPromise ??= Promise.all([
    new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      link.onload = resolve;
      link.onerror = reject;
      document.head.append(link);
    }),
    new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = jsUrl;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.append(script);
    }),
  ])
    .then(() => window.L)
    .catch((error) => {
      leafletPromise = undefined;
      throw error;
    });

  return leafletPromise;
}

/** Abre la conexión con el servidor de mosaicos mientras baja Leaflet (en modo automático ya
 * viene declarada en la sección). */
function preconnect(tiles) {
  try {
    const origin = new URL(tiles.url.replace('{s}', tiles.subdomains?.[0] ?? '')).origin;
    if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin; // sin crossorigin: los mosaicos se piden como imágenes normales
    document.head.append(link);
  } catch {
    /* URL de mosaicos inválida: no hace falta el preconnect */
  }
}

class StoreLocator extends HTMLElement {
  /** @type {AbortController | undefined} */
  #aborter;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #map;
  #L;
  #userMarker;
  #mapRequested = false;
  /** @type {Store | null} */
  #pendingFocus = null;
  /** @type {Store | null} */
  #active = null;
  #region = '';
  #userPosition = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hintTimer;
  #gestureZoom = 0;
  #touchStart = null;

  connectedCallback() {
    this.#aborter = new AbortController();
    const { signal } = this.#aborter;

    this.list = this.querySelector('[data-list]');
    this.searchInput = this.querySelector('[data-search]');
    this.countEl = this.querySelector('[data-count]');
    this.messageEl = this.querySelector('[data-message]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.mapWrap = this.querySelector('[data-map-wrap]');
    this.mapEl = this.querySelector('[data-map]');
    this.nearMeButton = this.querySelector('[data-near-me]');

    /** @typedef {{ id: string, card: HTMLElement, pin: HTMLElement | null, lat: number, lng: number, hasCoords: boolean, region: string, text: string, marker?: any }} Store */
    /** @type {Store[]} */
    this.stores = [...this.querySelectorAll('[data-store]')].map((card) => {
      const lat = Number.parseFloat(card.dataset.lat ?? '');
      const lng = Number.parseFloat(card.dataset.lng ?? '');
      const id = card.dataset.storeId ?? '';
      return {
        id,
        card,
        pin: this.querySelector(`[data-pin="${CSS.escape(id)}"]`),
        lat,
        lng,
        hasCoords: Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180,
        region: card.dataset.region ?? '',
        text: normalize(card.dataset.search),
      };
    });

    this.searchInput?.addEventListener('input', () => this.#applyFilters(), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });

    if (this.nearMeButton && 'geolocation' in navigator) {
      this.nearMeButton.hidden = false;
      this.nearMeButton.addEventListener('click', () => this.#locate(), { signal });
    }

    this.#setupMapLoading();
    this.#setupGestures(signal);
    this.#setupStickyToolbar(signal);

    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const store = this.stores.find((item) => item.card === event.target);
          if (store) this.#focusStore(store, { scroll: false });
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#aborter?.abort();
    this.#observer?.disconnect();
    this.#map?.remove();
    this.#map = undefined;
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);

    const chip = target.closest('[data-region-filter]');
    if (chip) {
      this.#setRegion(chip.getAttribute('data-region-filter') ?? '');
      return;
    }

    // Vista previa estática: un pin abre esa tienda; el resto carga el mapa.
    if (target.closest('[data-placeholder]')) {
      const pinId = target.closest('[data-pin]')?.getAttribute('data-pin');
      const pinStore = pinId && this.stores.find((item) => item.id === pinId);
      if (pinStore) this.#focusStore(pinStore, { scroll: false });
      else this.#loadMap();
      return;
    }

    // Enlaces (cómo llegar, teléfono, WhatsApp) siguen su curso normal.
    if (target.closest('a')) return;

    const card = target.closest('[data-store]');
    if (!card) return;
    const store = this.stores.find((item) => item.card === card);
    if (!store?.hasCoords) return;

    // "Ver en mapa" carga el mapa y lo trae a la vista; un clic en el resto de la tarjeta solo
    // centra el mapa si ya está cargado, sin mover la página.
    if (target.closest('[data-focus-store]')) this.#focusStore(store);
    else if (this.#map) this.#focusStore(store, { scroll: false });
  }

  /** @param {string} region */
  #setRegion(region) {
    this.#region = region;
    for (const chip of this.querySelectorAll('[data-region-filter]')) {
      chip.setAttribute('aria-pressed', String(chip.getAttribute('data-region-filter') === region));
    }
    this.#applyFilters();
  }

  #applyFilters() {
    const query = normalize(this.searchInput?.value);
    const terms = query.split(/\s+/).filter(Boolean);
    let visible = 0;

    for (const store of this.stores) {
      const match =
        (!this.#region || store.region === this.#region) && terms.every((term) => store.text.includes(term));
      store.card.hidden = !match;
      if (store.pin) store.pin.hidden = !match;
      if (match) visible += 1;
    }

    if (this.countEl) {
      const template = visible === 1 ? this.countEl.dataset.one : this.countEl.dataset.many;
      this.countEl.textContent = (template ?? '[n]').replace('[n]', String(visible));
    }
    if (this.emptyEl) this.emptyEl.hidden = visible > 0;

    this.#syncMarkers();
    this.#fitToVisible();
  }

  #locate() {
    const button = this.nearMeButton;
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    this.#setMessage('Buscando tu ubicación…');

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        button.removeAttribute('aria-busy');
        this.#userPosition = { lat: coords.latitude, lng: coords.longitude };
        this.#sortByDistance();
        this.#setMessage('Ordenadas de la más cercana a la más lejana.');
        this.#showUser();
      },
      (error) => {
        button.removeAttribute('aria-busy');
        this.#setMessage(
          error.code === error.PERMISSION_DENIED
            ? 'No tenemos permiso para ver tu ubicación. Actívalo en tu navegador para ver las tiendas más cercanas.'
            : 'No pudimos obtener tu ubicación. Inténtalo de nuevo.'
        );
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  #sortByDistance() {
    const position = this.#userPosition;
    if (!position || !this.list) return;

    const withDistance = this.stores.map((store) => ({
      store,
      km: store.hasCoords ? distanceKm(position.lat, position.lng, store.lat, store.lng) : Infinity,
    }));
    withDistance.sort((a, b) => a.km - b.km);

    for (const { store, km } of withDistance) {
      const label = store.card.querySelector('[data-distance]');
      if (label) {
        label.hidden = !Number.isFinite(km);
        label.textContent = Number.isFinite(km) ? formatDistance(km) : '';
      }
      this.list.append(store.card);
    }
    this.stores = withDistance.map(({ store }) => store);
  }

  /** @param {string} text */
  #setMessage(text) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.hidden = !text;
  }

  /**
   * Marca el buscador con `.is-stuck` cuando quedó fijo debajo del header (computadora), para
   * que recién ahí tape la franja de arriba.
   * @param {AbortSignal} signal
   */
  #setupStickyToolbar(signal) {
    const toolbar = this.querySelector('.store-locator__toolbar');
    if (!(toolbar instanceof HTMLElement)) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const top = Number.parseFloat(getComputedStyle(toolbar).top);
      // Con la página arriba el buscador está más abajo que su `top` fijo; solo llega ahí al
      // desplazarse.
      const stuck =
        getComputedStyle(toolbar).position === 'sticky' &&
        Number.isFinite(top) &&
        toolbar.getBoundingClientRect().top <= top + 1;
      toolbar.classList.toggle('is-stuck', stuck);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    // En captura sobre document: se entera del scroll de la ventana o de cualquier contenedor.
    document.addEventListener('scroll', schedule, { capture: true, passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    update();
  }

  /* ---------- Gestos ---------- */

  /** @param {AbortSignal} signal */
  #setupGestures(signal) {
    const wrap = this.mapWrap;
    if (!wrap) return;

    // Captura en el contenedor: decide antes que Leaflet si la rueda es para el mapa o la página.
    wrap.addEventListener('wheel', (event) => this.#onWheel(event), { capture: true, passive: false, signal });

    // Safari (macOS) manda el pellizco del trackpad como gesturestart/change/end.
    wrap.addEventListener('gesturestart', (event) => this.#onGesture(event), { signal });
    wrap.addEventListener('gesturechange', (event) => this.#onGesture(event), { signal });
    wrap.addEventListener('gestureend', (event) => this.#onGesture(event), { signal });

    // Un dedo sobre el mapa en celular: la página se desplaza normal y se avisa cómo mover el mapa.
    wrap.addEventListener(
      'touchstart',
      (event) => {
        this.#touchStart = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      },
      { passive: true, signal }
    );
    wrap.addEventListener(
      'touchmove',
      (event) => {
        const start = this.#touchStart;
        if (!start || event.touches.length !== 1 || !this.#map || !touchOnly()) return;
        const touch = event.touches[0];
        if (Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 12) {
          this.#touchStart = null;
          this.#showHint(TOUCH_HINT);
        }
      },
      { passive: true, signal }
    );
  }

  /** @param {WheelEvent} event */
  #onWheel(event) {
    if (event.ctrlKey || event.metaKey) {
      // Pellizco o Ctrl/⌘ + rueda: es para el mapa. Leaflet lo toma y cancela el zoom de la
      // página; si el mapa todavía no está, se cancela aquí y se pide el mapa.
      if (!this.#map) {
        event.preventDefault();
        this.#loadMap();
      }
      return;
    }

    // Rueda sola: que no llegue a Leaflet, así el navegador desplaza la página.
    event.stopPropagation();
    if (this.#map && Math.abs(event.deltaY) > Math.abs(event.deltaX)) this.#showHint(WHEEL_HINT);
  }

  /** @param {Event & { scale?: number, clientX?: number, clientY?: number }} event */
  #onGesture(event) {
    event.preventDefault(); // sin esto Safari hace zoom en la página
    const map = this.#map;
    if (!map) {
      this.#loadMap();
      return;
    }

    if (event.type === 'gesturestart') {
      this.#gestureZoom = map.getZoom();
      map.options.zoomSnap = 0; // zoom continuo mientras dura el pellizco
      return;
    }

    if (event.type === 'gesturechange' && event.scale) {
      const point = map.mouseEventToContainerPoint(event);
      map.setZoomAround(point, this.#gestureZoom + Math.log2(event.scale), { animate: false });
      return;
    }

    map.options.zoomSnap = 0.5;
    map.setZoom(Math.round(map.getZoom() * 2) / 2);
  }

  /** @param {string} text */
  #showHint(text) {
    const hint = this.querySelector('[data-gesture-hint]');
    if (!hint) return;
    hint.textContent = text;
    hint.classList.add('is-visible');
    clearTimeout(this.#hintTimer);
    this.#hintTimer = setTimeout(() => hint.classList.remove('is-visible'), 1200);
  }

  /* ---------- Mapa ---------- */

  #setupMapLoading() {
    if (!this.mapEl || this.dataset.mapLoad === 'click') return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#loadMap();
      },
      { rootMargin: '300px' }
    );
    this.#observer.observe(this.mapEl);
  }

  async #loadMap() {
    if (this.#mapRequested || !this.mapWrap) return;
    this.#mapRequested = true;
    this.#observer?.disconnect();
    this.mapWrap.dataset.state = 'loading';

    const tiles = TILES[this.dataset.tiles ?? ''] ?? TILES.osm;
    preconnect(tiles);

    try {
      const L = await loadLeaflet(this.dataset.leafletJs, this.dataset.leafletCss);
      if (!this.isConnected) return;
      const layer = this.#initMap(L, tiles);

      // Se cambia a "ready" (se desvanece la vista previa) cuando cargaron los mosaicos visibles,
      // o a los 2.5 s como máximo si alguno se demora.
      const wrap = this.mapWrap;
      const reveal = () => {
        clearTimeout(timer);
        if (wrap.dataset.state === 'loading') wrap.dataset.state = 'ready';
      };
      const timer = setTimeout(reveal, 2500);
      layer.once('load', reveal);
    } catch {
      // Queda la vista previa con "Reintentar".
      this.#mapRequested = false;
      this.mapWrap.dataset.state = 'error';
    }
  }

  #initMap(L, tiles) {
    this.#L = L;
    const map = L.map(this.mapEl, {
      // La rueda llega a Leaflet solo con Ctrl/⌘ o pellizco: el filtro está en #onWheel.
      scrollWheelZoom: true,
      // En pantallas táctiles un dedo es para la página; dos dedos mueven y hacen zoom (touchZoom).
      dragging: !touchOnly(),
      maxZoom: tiles.maxZoom,
      zoomSnap: 0.5,
    });
    this.#map = map;

    const layer = L.tileLayer(tiles.url, {
      attribution: tiles.attribution,
      subdomains: tiles.subdomains ?? 'abc',
      maxZoom: tiles.maxZoom,
    }).addTo(map);
    if (tiles.labels) L.tileLayer(tiles.labels, { maxZoom: tiles.maxZoom, pane: 'shadowPane' }).addTo(map);

    const icon = L.divIcon({
      className: 'store-locator__marker',
      html: PIN_SVG,
      // Mismo tamaño que los pines de la vista previa (.store-locator__static-pin) para que no
      // cambien al desvanecerse la imagen estática.
      iconSize: [26, 35],
      iconAnchor: [13, 35],
      popupAnchor: [0, -31],
    });

    for (const store of this.stores) {
      if (!store.hasCoords) continue;
      const name = store.card.querySelector('[data-name]')?.textContent?.trim() ?? '';
      store.marker = L.marker([store.lat, store.lng], { icon, title: name, alt: name, riseOnHover: true })
        .bindPopup(() => this.#popupContent(store), { maxWidth: 260, autoPanPadding: [24, 24] })
        .on('click', () => this.#setActive(store, { scrollList: true }));
    }

    this.#syncMarkers();
    if (!this.#matchStaticView(map)) this.#fitToVisible(false);
    if (this.#userPosition) this.#showUser();

    if (this.#pendingFocus) {
      const store = this.#pendingFocus;
      this.#pendingFocus = null;
      this.#focusStore(store, { scroll: false });
    }
    return layer;
  }

  /**
   * Arranca el mapa con el mismo encuadre que muestra la vista previa estática, para que al
   * desvanecerla no haya salto de zoom. Solo si el cliente no filtró todavía (si filtró, se
   * encuadran las tiendas visibles). Devuelve false si no aplica.
   */
  #matchStaticView(map) {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-static-bounds]'));
    const container = this.mapEl;
    if (!box || !container) return false;
    if (this.#region || this.searchInput?.value.trim()) return false;

    const [west, east, mercNorth, mercSouth] = (box.dataset.staticBounds ?? '').split(',').map(Number);
    const { width: boxW, height: boxH } = box.getBoundingClientRect();
    const { width, height } = container.getBoundingClientRect();
    if (![west, east, mercNorth, mercSouth].every(Number.isFinite) || !boxW || !boxH || !width || !height) return false;

    // La caja de la imagen está centrada y escalada como `cover`: se calcula qué parte se ve.
    const fx0 = (boxW - width) / 2 / boxW;
    const fx1 = (boxW + width) / 2 / boxW;
    const fy0 = (boxH - height) / 2 / boxH;
    const fy1 = (boxH + height) / 2 / boxH;
    const lng = (f) => west + f * (east - west);
    const lat = (f) => {
      const merc = mercNorth - f * (mercNorth - mercSouth);
      return ((2 * Math.atan(Math.exp(merc)) - Math.PI / 2) * 180) / Math.PI;
    };

    // Zoom fraccionario exacto (zoomSnap 0) solo para este encuadre inicial.
    const snap = map.options.zoomSnap;
    map.options.zoomSnap = 0;
    map.fitBounds(
      [
        [lat(fy1), lng(fx0)],
        [lat(fy0), lng(fx1)],
      ],
      { padding: [0, 0], animate: false }
    );
    map.options.zoomSnap = snap;
    return true;
  }

  /** @param {Store} store */
  #popupContent(store) {
    const { card } = store;
    const root = document.createElement('div');

    const add = (selector, className) => {
      const text = card.querySelector(selector)?.textContent?.trim();
      if (!text) return;
      const p = document.createElement('p');
      p.className = className;
      p.textContent = text;
      root.append(p);
    };
    add('[data-name]', 'store-locator__popup-name');
    add('[data-address]', '');
    add('[data-reference]', '');
    add('[data-hours]', '');

    const directions = /** @type {HTMLAnchorElement | null} */ (card.querySelector('[data-directions]'));
    if (directions) {
      const link = document.createElement('a');
      link.className = 'store-locator__popup-link';
      link.href = directions.href;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = `${directions.textContent?.trim()} →`;
      root.append(link);
    }
    return root;
  }

  #syncMarkers() {
    if (!this.#map) return;
    for (const store of this.stores) {
      if (!store.marker) continue;
      if (store.card.hidden) store.marker.remove();
      else if (!this.#map.hasLayer(store.marker)) store.marker.addTo(this.#map);
    }
  }

  #fitToVisible(animate = !reducedMotion()) {
    if (!this.#map) return;
    const points = this.stores
      .filter((store) => store.marker && !store.card.hidden)
      .map((store) => [store.lat, store.lng]);
    if (!points.length) return;

    if (points.length === 1) {
      this.#map.setView(points[0], Number(this.dataset.zoom) || 16, { animate });
    } else {
      this.#map.fitBounds(points, { padding: [40, 40], maxZoom: 15, animate });
    }
  }

  #showUser() {
    const L = this.#L;
    const position = this.#userPosition;
    if (!this.#map || !L || !position) return;

    const latLng = [position.lat, position.lng];
    if (this.#userMarker) this.#userMarker.setLatLng(latLng);
    else {
      this.#userMarker = L.marker(latLng, {
        icon: L.divIcon({ className: 'store-locator__user-marker', iconSize: [16, 16] }),
        title: 'Tu ubicación',
        keyboard: false,
        zIndexOffset: 1000,
      }).addTo(this.#map);
    }

    // Encuadra al cliente con sus 3 tiendas más cercanas (ya están ordenadas).
    const nearest = this.stores.filter((store) => store.hasCoords && !store.card.hidden).slice(0, 3);
    const points = [latLng, ...nearest.map((store) => [store.lat, store.lng])];
    this.#map.fitBounds(points, { padding: [48, 48], maxZoom: 15, animate: !reducedMotion() });
  }

  /**
   * @param {Store} store
   * @param {{ scroll?: boolean }} [options]
   */
  #focusStore(store, { scroll = true } = {}) {
    if (!store.hasCoords) return;
    this.#setActive(store);

    // En celular el mapa está arriba de la lista: se lleva a la vista.
    if (scroll && this.mapWrap) {
      const rect = this.mapWrap.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        this.mapWrap.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
      }
    }

    if (!this.#map) {
      this.#pendingFocus = store;
      this.#loadMap();
      return;
    }

    const zoom = Math.max(this.#map.getZoom(), Number(this.dataset.zoom) || 16);
    const open = () => store.marker?.openPopup();
    if (reducedMotion()) {
      this.#map.setView([store.lat, store.lng], zoom, { animate: false });
      open();
    } else {
      this.#map.once('moveend', open);
      this.#map.flyTo([store.lat, store.lng], zoom, { duration: 0.6 });
    }
  }

  /**
   * @param {Store} store
   * @param {{ scrollList?: boolean }} [options]
   */
  #setActive(store, { scrollList = false } = {}) {
    if (this.#active && this.#active !== store) {
      this.#active.card.classList.remove('is-active');
      this.#active.marker?.getElement()?.classList.remove('is-active');
    }
    this.#active = store;
    store.card.classList.add('is-active');
    store.marker?.getElement()?.classList.add('is-active');

    // Computadora: el mapa está fijo al costado, así que se trae la tarjeta a la vista (debajo
    // del header y del buscador fijos). En celular no se mueve la página, para no sacar al
    // cliente del mapa.
    if (scrollList && window.matchMedia('(min-width: 990px)').matches) {
      const headerHeight = Number.parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
      const toolbarBottom = this.querySelector('.store-locator__toolbar')?.getBoundingClientRect().bottom ?? 0;
      const top = Math.max(toolbarBottom, headerHeight) + 12;
      const rect = store.card.getBoundingClientRect();
      if (rect.top < top || rect.bottom > window.innerHeight - 12) {
        scrollParent(store.card).scrollBy({ top: rect.top - top, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    }
  }
}

if (!customElements.get('store-locator')) customElements.define('store-locator', StoreLocator);
